import {
  BookingType,
  type NotSerializedBookingInputs,
  type VisitorBookerInfo,
  type VisitorRegistrationEntry
} from "../typings/booking-inputs";
import type { TeamMember, VisitorInformation } from "../typings/team-member";

function generateVisitorEntryId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function createVisitorRegistrationEntry(): VisitorRegistrationEntry {
  return {
    id: generateVisitorEntryId(),
    firstName: "",
    surname: "",
    company: "",
    email: "",
    contacts: "",
    visitReason: "",
    bookPlace: false
  };
}

/**
 * Builds `usersBookedFor` for visitor mode: booker + one entry per invited visitor.
 * Stored state feeds POST payloads (see transformBookingUsers visitor branch).
 * For location desk-capacity checks, use {@link filterVisitorDeskDemandUsers} — host and visit-only guests do not count.
 */
export function buildVisitorUsersBookedFor(
  booker: VisitorBookerInfo,
  visitorRegistrations: VisitorRegistrationEntry[]
): TeamMember[] {
  const bookerRow: TeamMember = {
    userId: booker.sub,
    firstName: booker.firstName,
    surname: booker.surname,
    email: booker.email,
    isExternal: false,
    isVisitor: false,
    bookingInventoryId: undefined
  };

  const visitorRows: TeamMember[] = visitorRegistrations.map(v => {
    const visitorInformation: VisitorInformation = {
      firstName: v.firstName,
      surname: v.surname,
      company: v.company?.trim() ? v.company : undefined,
      email: v.email,
      contacts: v.contacts?.trim() ? v.contacts : undefined,
      visitReason: v.visitReason?.trim() ? v.visitReason : undefined
    };

    return {
      userId: booker.sub,
      firstName: v.firstName,
      surname: v.surname,
      email: v.email,
      company: v.company,
      isExternal: false,
      isVisitor: true,
      bookPlace: v.bookPlace,
      visitorInformation,
      bookingInventoryId: undefined
    };
  });

  return [bookerRow, ...visitorRows];
}

/** Visitor guests who need a desk (`bookPlace === true`). Excludes the host/booker row. */
export function filterVisitorDeskDemandUsers(
  usersBookedFor: TeamMember[] | undefined
): TeamMember[] {
  return (usersBookedFor ?? []).filter(user => user.isVisitor === true && user.bookPlace === true);
}

/** Desk demand for visitor-mode location availability (host and visit-only guests excluded). */
export function countVisitorDeskDemand(usersBookedFor: TeamMember[] | undefined): number {
  return filterVisitorDeskDemandUsers(usersBookedFor).length;
}

/** Empty visitor row for team invitation dialog (no placeholder personal data). */
export function createEmptyVisitorRegistrationEntry(): VisitorRegistrationEntry {
  return {
    id: generateVisitorEntryId(),
    firstName: "",
    surname: "",
    company: "",
    email: "",
    contacts: "",
    visitReason: "",
    bookPlace: false,
    visitOnlySelectedLocation: null,
    visitOnlyLocationName: null
  };
}

export function visitorRequestsPlaceOrRoom(inputs: NotSerializedBookingInputs): boolean {
  return Boolean(inputs.visitorRegistrations?.some(v => v.bookPlace === true));
}

export function areVisitorRegistrationsComplete(inputs: NotSerializedBookingInputs): boolean {
  const list = inputs.visitorRegistrations;
  if (!list?.length) {
    return false;
  }
  return list.every(v =>
    Boolean(v.firstName?.trim() && v.surname?.trim() && v.email?.trim() && v.visitReason?.trim())
  );
}

export function getDuplicateVisitorEmailIds(inputs: NotSerializedBookingInputs): Set<string> {
  const duplicates = new Set<string>();
  const emailToFirstRowId = new Map<string, string>();
  const list = inputs.visitorRegistrations ?? [];

  list.forEach(v => {
    const normalizedEmail = v.email?.trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }
    const existingRowId = emailToFirstRowId.get(normalizedEmail);
    if (existingRowId) {
      duplicates.add(existingRowId);
      duplicates.add(v.id);
      return;
    }
    emailToFirstRowId.set(normalizedEmail, v.id);
  });

  return duplicates;
}

export function hasDuplicateVisitorEmails(inputs: NotSerializedBookingInputs): boolean {
  return getDuplicateVisitorEmailIds(inputs).size > 0;
}

/** True when the row has no visitor data entered (default blank row in the invitation panel). */
export function isVisitorRegistrationRowEmpty(row: VisitorRegistrationEntry): boolean {
  return (
    !row.firstName?.trim() &&
    !row.surname?.trim() &&
    !row.email?.trim() &&
    !row.company?.trim() &&
    !row.contacts?.trim() &&
    !row.visitReason?.trim() &&
    row.visitOnlySelectedLocation == null &&
    !row.visitOnlyLocationName?.trim()
  );
}

export function filterSubstantiveVisitorRegistrations(
  registrations: VisitorRegistrationEntry[] | undefined
): VisitorRegistrationEntry[] {
  return (registrations ?? []).filter(row => !isVisitorRegistrationRowEmpty(row));
}

/** Conference invite step: block Next only when the user started visitor rows that are still incomplete. */
export type VisitorRegistrationStepCompleteOptions = {
  /** Conference invite step: visit location is derived from the room building at confirm. */
  skipVisitOnlyLocation?: boolean;
};

export function conferenceInviteVisitorStepBlocked(inputs: NotSerializedBookingInputs): boolean {
  const substantive = filterSubstantiveVisitorRegistrations(inputs.visitorRegistrations);
  if (substantive.length === 0) {
    return false;
  }
  return !isVisitorRegistrationStepComplete(
    {
      ...inputs,
      visitorRegistrations: substantive
    },
    { skipVisitOnlyLocation: true }
  );
}

export function isVisitorRegistrationStepComplete(
  inputs: NotSerializedBookingInputs,
  options?: VisitorRegistrationStepCompleteOptions
): boolean {
  if (!areVisitorRegistrationsComplete(inputs)) {
    return false;
  }
  if (hasDuplicateVisitorEmails(inputs)) {
    return false;
  }
  const list = inputs.visitorRegistrations;
  if (!list?.length) {
    return false;
  }
  if (options?.skipVisitOnlyLocation) {
    return true;
  }
  for (const v of list) {
    if (v.bookPlace !== true) {
      if (v.visitOnlySelectedLocation == null || !v.visitOnlyLocationName?.trim()) {
        return false;
      }
    }
  }
  return true;
}

/** Sync `bookingType` from visitor rows that book a workplace (for location/floor APIs). */
export function bookingTypeFromVisitorRegistrations(
  inputs: NotSerializedBookingInputs
): BookingType | null {
  const regs = inputs.visitorRegistrations;
  if (!regs?.some(v => v.bookPlace === true)) {
    return null;
  }
  return BookingType.WORKPLACE;
}
